import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Check,
  CheckSquare,
  Search,
  X,
  ChevronDown,
  Loader2,
  Calendar,
  Upload,
  FileText,
  AlertCircle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
} from "lucide-react";
import { FaArrowLeft } from "react-icons/fa";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { getRegistrationDateError } from "../../utils/registrationDateFilter";
import { REGISTRATION_STATUS_FILTER_OPTIONS } from "../../utils/registrationStatus";
import { getRsvpBroadcastAudienceOptions } from "../../services/rsvp";
import { getAllTicketsForEvent8, validateSubmissionIds } from "../../services/ticket";
import { getEventForms } from "../../services/forms";
import { getAgendaAccessOptions } from "../../services/agenda";

const REGISTRATION_MODE_OPTIONS = [
  { value: "disabled", label: "Disable all access (No attendees)" },
  { value: "all", label: "Open to all (All attendees)" },
  { value: "ticket_class", label: "Ticket class wise" },
  { value: "registration_status", label: "Registration status wise" },
  { value: "date_time", label: "Date & time wise" },
  { value: "custom_form", label: "Custom Form" },
  { value: "checked_in", label: "Checked-in registrations" },
  { value: "selected", label: "Selected registrations" },
];

const CUSTOM_FORM_STATUS_OPTIONS = [
  { value: "not_filled", label: "Not Filled" },
  { value: "filled", label: "Filled" },
];

const defaultAudience = {
  openToAll: false,
  ticketClassIds: [],
  zoneIds: [],
  registrationMode: "disabled",
  registrationStatusFilter: "shortlisted",
  selectedRegistrationIds: [],
  excludedRegistrationIds: [],
  allRegistrationsExcluded: false,
  registrationSearch: "",
  registrationDateFrom: "",
  registrationDateTo: "",
  customFormId: "",
  customFormStatus: "not_filled",
};

const normalizeAudienceIds = (items = [], fallbackKey = "id") =>
  (Array.isArray(items) ? items : [])
    .map((item) => String(item?.[fallbackKey] || item?.id || item))
    .filter(Boolean);

const fillAudience = (audienceData = {}, savedConfig = {}) => {
  const ticketRows = audienceData.ticketClasses || audienceData.ticket_classes || [];
  const zoneRows = audienceData.zones || [];
  const config = savedConfig || {};
  const selectedTicketClassIds =
    Array.isArray(config.selectedTicketClassIds)
      ? normalizeAudienceIds(config.selectedTicketClassIds)
      : normalizeAudienceIds(config.ticketClassIds).length > 0
      ? normalizeAudienceIds(config.ticketClassIds)
      : config.registrationMode === "custom_form" ||
        config.registrationMode === "registration_status" ||
        config.registrationMode === "import"
      ? []
      : ticketRows.map((item) => String(item.ticket_class_id || item.id || item));

  let registrationMode = config.registrationMode;
  if (!registrationMode) {
    if (config.openToAll === true) {
      registrationMode = "all";
    } else {
      registrationMode = "disabled";
    }
  } else if (registrationMode === "none") {
    registrationMode = "disabled";
  }

  // Removed message-status modes must not silently grant broader access.
  if (["template_filter", "date_time_and_template"].includes(registrationMode)) {
    registrationMode = "disabled";
  }
  const isDateMode = registrationMode === "date_time";
  const wasImport = registrationMode === "import";
  if (wasImport) registrationMode = "selected";
  const excluded = new Set(normalizeAudienceIds(config.excludedRegistrationIds));
  const importedIds = (Array.isArray(config.importedRegistrations) ? config.importedRegistrations : [])
    .map(row => String(row.id || "")).filter(id => id && !excluded.has(id));
  const selectedIds = wasImport
    ? (config.allRegistrationsExcluded || config.all_registrations_excluded ? [] : importedIds)
    : normalizeAudienceIds(config.selectedRegistrationIds);

  return {
    openToAll: registrationMode === "all",
    ticketClassIds: selectedTicketClassIds,
    zoneIds: zoneRows.map((item) => String(item.zone_id || item.id || item)),
    registrationMode,
    registrationStatusFilter: config.registrationStatusFilter || config.registration_status_filter || "shortlisted",
    selectedRegistrationIds: [...new Set(selectedIds)],
    excludedRegistrationIds: wasImport ? [] : normalizeAudienceIds(config.excludedRegistrationIds),
    allRegistrationsExcluded: wasImport ? false : Boolean(config.allRegistrationsExcluded ?? config.all_registrations_excluded ?? false),
    registrationSearch: "",
    registrationDateFrom: isDateMode ? (config.registrationDateFrom || config.registration_date_from || "") : "",
    registrationDateTo: isDateMode ? (config.registrationDateTo || config.registration_date_to || "") : "",
    customFormId: config.customFormId || config.custom_form_id || "",
    customFormStatus: config.customFormStatus || config.custom_form_status || "not_filled",
  };
};

const isUrlLike = (value = "") => /^https?:\/\//i.test(String(value || "").trim());

const audiencePersonName = (person = {}) =>
  [person.first_name, person.last_name].filter(Boolean).join(" ").trim() ||
  person.name ||
  (isUrlLike(person.email) ? "" : person.email) ||
  person.whatsapp_number ||
  "Unnamed";

const audienceSearchText = (person = {}) =>
  [
    audiencePersonName(person),
    person.search_text,
    person.form_search_text,
    person.email,
    person.whatsapp_number,
    person.ticket_class_name,
    person.submission_id,
    person.company_name,
    person.designation,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const filterAudienceRows = (rows = [], search = "") => {
  const query = String(search || "").trim().toLowerCase();
  if (!query) return rows;
  return rows.filter((row) => audienceSearchText(row).includes(query));
};

const audiencePersonDetails = (person = {}) => {
  const parts = [];
  if (person.email && !isUrlLike(person.email)) parts.push(person.email);
  if (person.whatsapp_number) parts.push(person.whatsapp_number);
  if (person.ticket_class_name) parts.push(person.ticket_class_name);
  if (person.submission_id) parts.push(`Submission Id: #${person.submission_id}`);
  return parts.join(" • ");
};

const CustomSelect = ({
  options = [],
  value,
  onChange,
  placeholder = "Select an option",
  disabled = false,
  className = "",
  size = "md",
  searchable = false,
  searchPlaceholder = "Search...",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && searchable) {
      setTimeout(() => searchInputRef.current?.focus(), 40);
    } else {
      setSearchQuery("");
    }
  }, [isOpen, searchable]);

  const selectedOption = options.find((opt) => String(opt.value) === String(value));
  const isSm = size === "sm";

  const displayedOptions = useMemo(() => {
    if (!searchable || !searchQuery.trim()) return options;
    const q = searchQuery.toLowerCase().trim();
    return options.filter(
      (opt) =>
        String(opt.label || "").toLowerCase().includes(q) ||
        String(opt.value || "").toLowerCase().includes(q) ||
        String(opt.detail || "").toLowerCase().includes(q)
    );
  }, [options, searchable, searchQuery]);

  return (
    <div className={`relative w-full ${className}`} ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white font-medium text-gray-800 transition-all text-left outline-none hover:border-gray-300 focus:border-blue-600 focus:ring-1 focus:ring-blue-100 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed ${
          isSm ? "h-9 px-3 text-xs" : "h-10 px-3.5 text-sm"
        }`}
      >
        <span className="truncate">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-gray-400 transition-transform flex-shrink-0 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && !disabled && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl flex flex-col">
          {searchable && (
            <div className="p-2 border-b border-gray-100 bg-slate-50/50">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full rounded-md border border-gray-200 bg-white py-1.5 pl-8 pr-7 text-xs outline-none focus:border-blue-600"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          )}
          <div className="flex-1 overflow-y-auto p-1 divide-y divide-gray-50">
            {displayedOptions.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-gray-400 italic">
                No options found
              </div>
            ) : (
              displayedOptions.map((opt) => {
                const isSelected = String(opt.value) === String(value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      setIsOpen(false);
                    }}
                    className={`w-full flex items-center justify-between rounded-lg px-3 py-2 text-left text-xs transition-colors ${
                      isSelected
                        ? "bg-blue-50 text-blue-700 font-bold"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <div className="truncate">
                      <div>{opt.label}</div>
                      {opt.detail && (
                        <div className="text-[10px] text-gray-400 font-normal">
                          {opt.detail}
                        </div>
                      )}
                    </div>
                    {isSelected && <Check className="h-3.5 w-3.5 text-blue-600 flex-shrink-0 ml-2" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CheckboxGrid = ({
  title,
  items = [],
  selectedIds = [],
  onChange,
  loading = false,
  emptyText = "No items found",
  getLabel = (item) => item.name || item.title || item.label || String(item),
  getId = (item) => String(item.id || item.value || item),
}) => {
  const allSelected =
    items.length > 0 && items.every((item) => selectedIds.includes(getId(item)));

  const handleToggleAll = () => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(items.map(getId));
    }
  };

  const handleToggle = (id) => {
    const sId = String(id);
    if (selectedIds.includes(sId)) {
      onChange(selectedIds.filter((x) => x !== sId));
    } else {
      onChange([...selectedIds, sId]);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
      <div className="flex items-center justify-between border-b border-gray-100 pb-2.5">
        <span className="text-xs font-bold text-gray-700 tracking-wide uppercase">
          {title} ({selectedIds.length} of {items.length})
        </span>
        {items.length > 0 && (
          <button
            type="button"
            onClick={handleToggleAll}
            className="text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
          >
            {allSelected ? "Deselect All" : "Select All"}
          </button>
        )}
      </div>

      {loading ? (
        <div className="py-6 text-center text-xs text-gray-400 flex items-center justify-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-blue-600" /> Loading...
        </div>
      ) : items.length === 0 ? (
        <div className="py-6 text-center text-xs text-gray-400 italic">
          {emptyText}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto pr-1">
          {items.map((item) => {
            const id = getId(item);
            const checked = selectedIds.includes(id);
            return (
              <label
                key={id}
                className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 text-xs font-medium cursor-pointer transition-all ${
                  checked
                    ? "border-blue-500 bg-blue-50/60 text-blue-900 shadow-2xs"
                    : "border-gray-200 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => handleToggle(id)}
                  className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                />
                <span className="truncate">{getLabel(item)}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

const AudiencePersonList = ({
  rows = [],
  selectedIds = [],
  onChange,
  emptyText = "No records found.",
  invertSelection = false,
  allExcluded = false,
  isRegistration = true,
  cols = 1,
  disabled = false,
}) => {
  const selectedIdSet = useMemo(() => new Set((selectedIds || []).map(String)), [selectedIds]);

  const handleRowClick = (id) => {
    if (disabled) return;
    const sId = String(id);
    if (invertSelection) {
      if (allExcluded) {
        onChange(null, sId, true);
        return;
      }
      const isCurrentlyExcluded = selectedIdSet.has(sId);
      if (isCurrentlyExcluded) {
        onChange((selectedIds || []).map(String).filter((item) => item !== sId), sId, true);
      } else {
        onChange([...(selectedIds || []).map(String), sId], sId, false);
      }
      return;
    }
    if (selectedIdSet.has(sId)) {
      onChange((selectedIds || []).map(String).filter((item) => item !== sId), sId, false);
    } else {
      onChange([...(selectedIds || []).map(String), sId], sId, true);
    }
  };

  if (!rows.length) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 p-8 text-center text-xs text-gray-400 italic">
        {emptyText}
      </div>
    );
  }

  return (
    <div
      className={`grid gap-2 max-h-72 overflow-y-auto pr-1 ${
        cols === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"
      }`}
    >
      {rows.map((person) => {
        const id = String(person.id);
        const inSelected = selectedIdSet.has(id);
        const isChecked = invertSelection
          ? allExcluded
            ? false
            : !inSelected
          : inSelected;
        const rawSubId = person.submission_id || person.submissionId;
        const subId = (rawSubId && rawSubId !== "-" && rawSubId !== "null" && rawSubId !== "undefined") ? rawSubId : person.id;

        return (
          <div
            key={id}
            onClick={() => handleRowClick(id)}
            className={`flex items-center justify-between gap-3 rounded-lg border p-2.5 transition-all cursor-pointer ${
              isChecked
                ? "border-blue-500 bg-blue-50/50 text-blue-950 shadow-2xs"
                : "border-gray-200 bg-white text-gray-700 hover:bg-slate-50/80"
            }`}
          >
            <div className="min-w-0 flex items-center gap-2.5">
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => {}}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0 pointer-events-none"
              />
              <div className="min-w-0">
                <div className="text-xs font-bold truncate">
                  {audiencePersonName(person)}
                </div>
                {isRegistration ? (
                  subId ? (
                    <div className="text-[11px] text-gray-500 truncate">
                      Submission Id: {subId}
                    </div>
                  ) : null
                ) : (
                  <div className="text-[11px] text-gray-500 truncate">
                    {audiencePersonDetails(person)}
                  </div>
                )}
              </div>
            </div>
            {!isRegistration && person.type && (
              <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600 flex-shrink-0">
                {person.type}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

const inputClass =
  "w-full bg-white border border-gray-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-100 rounded-lg px-4 py-2.5 text-sm transition-all outline-none disabled:bg-gray-50 disabled:text-gray-400";

const compactInputClass =
  "w-full bg-white border border-gray-200 focus:border-blue-600 focus:ring-1 focus:ring-blue-100 rounded-lg px-3 py-1.5 text-xs font-medium transition-all outline-none disabled:bg-gray-50 disabled:text-gray-400";

export const SessionAssigneeModal = ({
  sessionTarget,
  zoneTarget,
  target,
  eventId,
  token,
  eventTz = "Asia/Kolkata",
  onClose,
  onSave,
  entityType = "Session",
}) => {
  const currentTarget = zoneTarget || sessionTarget || target;
  const isZone = entityType?.toLowerCase() === "zone" || Boolean(zoneTarget);
  const targetTypeLabel = isZone ? "Zone" : "Session";
  const targetPluralLabel = isZone ? "Zones" : "Sessions";

  const audienceCategory = "registrations";
  const [audience, setAudience] = useState(defaultAudience);
  const [audienceOptions, setAudienceOptions] = useState({
    registrations: [],
    guests: [],
    registered_members: [],
    members: [],
    all_registration_ids: [],
  });
  const [audienceOptionsMeta, setAudienceOptionsMeta] = useState({});
  const [audienceOptionsLoading, setAudienceOptionsLoading] = useState(false);
  const [registrationSearchLoading, setRegistrationSearchLoading] = useState(false);
  const [registrationPage, setRegistrationPage] = useState(1);
  const [ticketClasses, setTicketClasses] = useState([]);
  const [eventForms, setEventForms] = useState([]);
  const [saving, setSaving] = useState(false);

  const audienceRequestId = useRef(0);

  const [importLoading, setImportLoading] = useState(false);
  const [importValidating, setImportValidating] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importInvalidIds, setImportInvalidIds] = useState([]);
  const [importPanelOpen, setImportPanelOpen] = useState(false);
  const [lastImport, setLastImport] = useState(null);
  const [legacyImportLoading, setLegacyImportLoading] = useState(false);
  const [legacyImportError, setLegacyImportError] = useState(false);
  const [legacyRetry, setLegacyRetry] = useState(0);
  const importContextRef = useRef(0);
  const [importInputMode, setImportInputMode] = useState("file"); // "file" | "paste"
  const [pasteText, setPasteText] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const importDialogRef = useRef(null);
  const importBusyRef = useRef(false);
  importBusyRef.current = importLoading || importValidating;
  const importPopupOpen = audience.registrationMode === "selected" && importPanelOpen;

  const openImportPopup = (mode) => {
    setImportInputMode(mode);
    setImportError(null);
    setImportInvalidIds([]);
    setLastImport(null);
    setImportPanelOpen(true);
  };
  const closeImportPopup = () => {
    if (!importBusyRef.current) setImportPanelOpen(false);
  };

  useEffect(() => {
    if (!importPopupOpen) return;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const dialog = importDialogRef.current;
    (dialog?.querySelector("textarea") || dialog)?.focus();
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (!importBusyRef.current) setImportPanelOpen(false);
      }
      if (event.key === "Tab" && dialog) {
        const items = Array.from(dialog.querySelectorAll('button:not([disabled]), textarea:not([disabled]), input:not([disabled]), [tabindex="0"]'))
          .filter(item => !item.classList.contains("hidden"));
        if (!items.length) { event.preventDefault(); dialog.focus(); return; }
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog)) {
          event.preventDefault(); first.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [importPopupOpen, importInputMode]);

  const pastedIdCount = useMemo(() => {
    const raw = pasteText.trim();
    if (!raw) return 0;
    return Array.from(new Set(raw.split(/[\r\n,;\s]+/).map((s) => s.trim()).filter(Boolean))).length;
  }, [pasteText]);

  // Convert old import audiences to the same explicit selection used by manual picking.
  useEffect(() => {
    const context = ++importContextRef.current;
    let active = true;
    setImportPanelOpen(false);
    setImportLoading(false);
    setImportValidating(false);
    setRegistrationPage(1);
    setLastImport(null);
    setImportError(null);
    setImportInvalidIds([]);
    setPasteText("");
    setLegacyImportError(false);
    setLegacyImportLoading(false);
    if (!currentTarget) return;
    let config = currentTarget.audience_config;
    if (typeof config === "string") {
      try { config = JSON.parse(config); } catch { config = null; }
    }
    if (!config) {
      const access = Array.isArray(currentTarget.access) ? currentTarget.access : [];
      const rule = access.find(item => item?.role === "attendee") || {};
      const disabled = access.some(item => ["none", "disabled"].includes(item?.role) || item?.scope === "none");
      const subIds = Array.isArray(rule.submission_ids) ? rule.submission_ids : [];
      const regIds = Array.isArray(rule.registration_ids) ? rule.registration_ids : [];
      const ticketIds = Array.isArray(rule.ticket_class_ids) ? rule.ticket_class_ids : [];
      config = {
        registrationMode: disabled ? "disabled" : access.some(item => item?.role === "all") ? "all"
          : subIds.length ? "import" : regIds.length ? "selected" : ticketIds.length ? "ticket_class" : "disabled",
        importedSubmissionIds: subIds, selectedRegistrationIds: regIds, ticketClassIds: ticketIds,
      };
    }
    setAudience(fillAudience({}, config));
    const subIds = config.importedSubmissionIds || config.submission_ids || [];
    if (config.registrationMode === "import" && subIds.length &&
        !config.allRegistrationsExcluded && !config.all_registrations_excluded) {
      setLegacyImportLoading(true);
      validateSubmissionIds(eventId, subIds, token).then(res => {
        if (!active) return;
        if ((res?.invalid_submission_ids || []).length) throw new Error("Some previously imported IDs could not be resolved.");
        const excluded = new Set(normalizeAudienceIds(config.excludedRegistrationIds));
        const ids = (res?.valid_registrations || []).map(row => String(row.id || ""))
          .filter(id => id && !excluded.has(id));
        if (!ids.length && !(res?.valid_registrations || []).length) throw new Error("Could not load the previously imported registrations.");
        setAudience(prev => ({ ...prev, selectedRegistrationIds: [...new Set([...prev.selectedRegistrationIds, ...ids])] }));
      }).catch(() => {
        if (active) setLegacyImportError(true);
      }).finally(() => {
        if (active) setLegacyImportLoading(false);
      });
    }
    return () => {
      active = false;
      if (importContextRef.current === context) ++importContextRef.current;
    };
  }, [currentTarget, eventId, token, legacyRetry]);

  const downloadSampleCsv = () => {
    const csvContent = "Submission Id\nSUB1001\nSUB1002\nSUB1003\n";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "sample_submission_id.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const validateAndApplySubmissionIds = async (submissionIds, fileName = "Pasted IDs") => {
    const context = importContextRef.current;
    setImportValidating(true);
    setLastImport(null);
    setImportError(null);
    setImportInvalidIds([]);
    try {
      const res = await validateSubmissionIds(eventId, submissionIds, token);
      if (context !== importContextRef.current) return false;
      const invalidList = res?.invalid_submission_ids || [];
      const validList = res?.valid_registrations || [];
      if (invalidList.length || !validList.length) {
        setImportInvalidIds(invalidList);
        const message = invalidList.length
          ? `${invalidList.length} Submission Id(s) do not belong to this event. Fix them and try again. Your selection has not changed.`
          : "None of the Submission Ids were found for this event. Your selection has not changed.";
        setImportError(message);
        toast.error(message);
        return false;
      }
      const ids = validList.map(row => String(row.id || "")).filter(Boolean);
      if (!ids.length) throw new Error("No valid registration records returned. Your selection has not changed.");
      setAudience(prev => prev.registrationMode !== "selected" ? prev : ({
        ...prev,
        selectedRegistrationIds: [...new Set([...prev.selectedRegistrationIds.map(String), ...ids])],
        allRegistrationsExcluded: false,
        excludedRegistrationIds: [],
      }));
      setLastImport({ fileName, count: new Set(ids).size });
      setPasteText("");
      toast.success(`${new Set(ids).size} validated registrations added to your selection.`);
      return true;
    } catch (err) {
      if (context !== importContextRef.current) return false;
      const message = err?.response?.data?.message || err?.message || "Failed to validate Submission Ids.";
      setImportError(message);
      toast.error(message);
      return false;
    } finally {
      if (context === importContextRef.current) setImportValidating(false);
    }
  };

  const handleFileUpload = async (e) => {
    if (importLoading || importValidating || legacyImportLoading) return;
    const context = importContextRef.current;
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = null;

    const isCsv =
      file.name.toLowerCase().endsWith(".csv") ||
      file.type === "text/csv" ||
      file.type === "application/csv";
    if (!isCsv) {
      toast.error("Only CSV files (.csv) are supported.");
      return;
    }

    setImportLoading(true);
    setLastImport(null);
    setImportError(null);
    setImportInvalidIds([]);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const firstSheetName = workbook.SheetNames[0];
      if (!firstSheetName) {
        throw new Error("No sheet found in the uploaded file.");
      }
      const worksheet = workbook.Sheets[firstSheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "" });

      if (!jsonData || jsonData.length === 0) {
        throw new Error("The uploaded file is empty.");
      }

      let subIdColIdx = -1;
      const headerRow = jsonData[0] || [];
      const subIdRegex = /^(submission[\s_-]?id|sub[\s_-]?id|submission|id|reg[\s_-]?id|registration[\s_-]?id|ticket[\s_-]?number)$/i;
      for (let c = 0; c < headerRow.length; c++) {
        const h = String(headerRow[c] || "").trim();
        if (subIdRegex.test(h)) {
          subIdColIdx = c;
          break;
        }
      }

      const startRow = subIdColIdx >= 0 ? 1 : 0;
      const colIdx = subIdColIdx >= 0 ? subIdColIdx : 0;

      const extracted = [];
      for (let r = startRow; r < jsonData.length; r++) {
        const row = jsonData[r];
        if (!row) continue;
        const rawVal = String(row[colIdx] ?? "").trim();
        if (rawVal && !subIdRegex.test(rawVal)) {
          extracted.push(rawVal);
        }
      }

      const uniqueIds = Array.from(new Set(extracted));
      if (!uniqueIds.length) {
        throw new Error("No Submission Ids found in the file.");
      }

      if (context === importContextRef.current) await validateAndApplySubmissionIds(uniqueIds, file.name);
    } catch (err) {
      if (context !== importContextRef.current) return;
      setImportError(err.message || "Failed to parse file.");
      toast.error(err.message || "Failed to parse file.");
    } finally {
      if (context === importContextRef.current) setImportLoading(false);
    }
  };

  const handlePasteSubmit = async () => {
    if (importLoading || importValidating || legacyImportLoading) return;
    const raw = pasteText.trim();
    if (!raw) {
      toast.error("Please enter at least one Submission Id.");
      return;
    }
    const ids = Array.from(
      new Set(
        raw
          .split(/[\r\n,;\s]+/)
          .map((s) => s.trim())
          .filter(Boolean)
      )
    );
    if (!ids.length) {
      toast.error("No valid Submission Ids found in text.");
      return;
    }
    const success = await validateAndApplySubmissionIds(ids, `Pasted (${ids.length} Submission Ids)`);
    if (success) {
      setImportPanelOpen(false);
    }
  };

  // Load ticket classes and forms
  useEffect(() => {
    if (!eventId) return;

    let mounted = true;
    // Populate each dropdown as soon as its own response arrives.
    getAllTicketsForEvent8(`event_id=${encodeURIComponent(eventId)}`)
      .then((res) => {
        const rows = res?.data?.data || res?.data;
        if (!Array.isArray(rows)) throw new Error("Invalid ticket response");
        if (mounted) setTicketClasses(rows);
      })
      .catch(async () => {
        const res = await getAgendaAccessOptions(eventId, token).catch(() => null);
        const rows = res?.data?.data || [];
        if (mounted) setTicketClasses(Array.isArray(rows) ? rows : []);
      });

    getEventForms(eventId).then((res) => {
      const rows = res?.data?.data || res?.data || [];
      if (mounted) setEventForms(Array.isArray(rows) ? rows : []);
    }).catch(() => {});

    return () => {
      mounted = false;
    };
  }, [eventId, token]);

  const updateAudience = (key, value) => {
    if (["registrationMode", "registrationSearch", "ticketClassIds", "registrationStatusFilter", "registrationDateFrom", "registrationDateTo", "customFormId", "customFormStatus"].includes(key)) setRegistrationPage(1);
    if (key === "registrationMode") {
      setImportPanelOpen(false);
      if (value !== audience.registrationMode) setLegacyImportError(false);
    }
    setAudience((prev) => ({ ...prev, [key]: value }));
  };

  const selectedTicketClassQuery = audience.ticketClassIds.map(String).sort().join(",");

  const prevFilterRef = useRef({
    mode: audience.registrationMode,
    ticketClasses: selectedTicketClassQuery,
    dateFrom: audience.registrationDateFrom,
    dateTo: audience.registrationDateTo,
    customFormId: audience.customFormId,
    customFormStatus: audience.customFormStatus,
    registrationStatusFilter: audience.registrationStatusFilter,
  });

  useEffect(() => {
    const prev = prevFilterRef.current;
    const current = {
      mode: audience.registrationMode,
      ticketClasses: selectedTicketClassQuery,
      dateFrom: audience.registrationDateFrom,
      dateTo: audience.registrationDateTo,
      customFormId: audience.customFormId,
      customFormStatus: audience.customFormStatus,
      registrationStatusFilter: audience.registrationStatusFilter,
    };

    const hasChanged =
      prev.mode !== current.mode ||
      prev.ticketClasses !== current.ticketClasses ||
      prev.dateFrom !== current.dateFrom ||
      prev.dateTo !== current.dateTo ||
      prev.customFormId !== current.customFormId ||
      prev.customFormStatus !== current.customFormStatus ||
      prev.registrationStatusFilter !== current.registrationStatusFilter;

    prevFilterRef.current = current;

    if (hasChanged && ((audience.excludedRegistrationIds || []).length > 0 || audience.allRegistrationsExcluded)) {
      setAudience((a) => ({ ...a, excludedRegistrationIds: [], allRegistrationsExcluded: false }));
    }
  }, [
    audience.registrationMode,
    selectedTicketClassQuery,
    audience.registrationDateFrom,
    audience.registrationDateTo,
    audience.customFormId,
    audience.customFormStatus,
    audience.registrationStatusFilter,
    audience.excludedRegistrationIds?.length,
    audience.allRegistrationsExcluded,
  ]);

  // Load audience options from server
  useEffect(() => {
    const requestId = ++audienceRequestId.current;
    if (!eventId) return;

    const controller = new AbortController();
    const isRegSearch = Boolean(audience.registrationSearch?.trim());
    if (isRegSearch) setRegistrationSearchLoading(true);
    else setAudienceOptionsLoading(true);

    const timer = setTimeout(
      () => {
        getRsvpBroadcastAudienceOptions(eventId, {
          audience_scope: "registrations",
          checked_in_only: audience.registrationMode === "checked_in" ? "1" : "",
          registration_search: audience.registrationSearch?.trim() || "",
          limit: 50,
          page: registrationPage,
          ticket_class_ids:
            ["ticket_class", "checked_in", "custom_form", "registration_status"].includes(audience.registrationMode)
              ? selectedTicketClassQuery
              : "",
          registration_status_filter:
            audience.registrationMode === "registration_status"
              ? audience.registrationStatusFilter || "shortlisted"
              : "",
          registration_date_from: audience.registrationMode === "date_time"
            ? audience.registrationDateFrom
            : "",
          registration_date_to: audience.registrationMode === "date_time"
            ? audience.registrationDateTo
            : "",
          custom_form_id:
            audience.registrationMode === "custom_form" ? audience.customFormId || "" : "",
          custom_form_status:
            audience.registrationMode === "custom_form" ? audience.customFormStatus || "" : "",
        }, { signal: controller.signal })
          .then((res) => {
            if (controller.signal.aborted || requestId !== audienceRequestId.current) return;
            const data = res?.data?.data?.audience || res?.data?.data || res?.data || {};
            const allMatchingIds = Array.isArray(data.all_registration_ids)
              ? data.all_registration_ids
              : Array.isArray(data.meta?.all_registration_ids)
              ? data.meta.all_registration_ids
              : [];
            setAudienceOptions({
              registrations: data.registrations || [],
              all_registration_ids: allMatchingIds,
              guests: data.guests || [],
              registered_members: data.registered_members || [],
              members: data.members || [],
            });
            setAudienceOptionsMeta(data.meta || {});
          })
          .catch((err) => {
            if (controller.signal.aborted || requestId !== audienceRequestId.current) return;
            toast.error("Failed to load audience records");
          })
          .finally(() => {
            if (controller.signal.aborted || requestId !== audienceRequestId.current) return;
            setAudienceOptionsLoading(false);
            setRegistrationSearchLoading(false);
          });
      },
      isRegSearch ? 300 : 0
    );

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [
    eventId,
    audience.registrationSearch,
    audience.registrationMode,
    selectedTicketClassQuery,
    audience.registrationStatusFilter,
    audience.registrationDateFrom,
    audience.registrationDateTo,
    audience.customFormId,
    audience.customFormStatus,
    registrationPage,
  ]);

  const filteredRegistrations = useMemo(() => {
    return filterAudienceRows(
      audienceOptions.registrations || [],
      audience.registrationSearch
    );
  }, [audienceOptions.registrations, audience.registrationSearch]);

  const displayedRegistrationRows = filteredRegistrations;

  const allMatchingRegistrationIds = useMemo(() => {
    const ids = audienceOptions.all_registration_ids;
    if (Array.isArray(ids) && ids.length > 0) {
      return ids.map(String);
    }
    return (audienceOptions.registrations || []).map((r) => String(r.id));
  }, [
    audience.registrationMode,
    audienceOptions.all_registration_ids,
    audienceOptions.registrations,
  ]);

  const registrationMeta = audienceOptionsMeta.registrations || {
    total_count: audienceOptionsMeta.registration_total_count,
    scope_count: audienceOptionsMeta.registration_scope_count,
    filtered_count: audienceOptionsMeta.registration_filtered_count,
    page: audienceOptionsMeta.registrations_page,
    has_more: audienceOptionsMeta.registrations_has_more,
  };
  const registrationTotalCount =
    registrationMeta.total_count ??
    (audienceOptions.registrations || []).length;
  const registrationScopeCount =
    registrationMeta.scope_count ??
    (audience.ticketClassIds.length
      ? (audienceOptions.registrations || []).length
      : registrationTotalCount);
  const registrationFilteredCount =
    registrationMeta.filtered_count ??
    (audience.registrationSearch?.trim()
      ? filteredRegistrations.length
      : registrationScopeCount);
  const registrationCurrentPage = registrationMeta.page ?? registrationPage;

  const isFilterRegMode = [
    "ticket_class",
    "registration_status",
    "date_time",
    "checked_in",
    "custom_form",
  ].includes(audience.registrationMode);

  const excludedRegistrationCount = isFilterRegMode
    ? audience.allRegistrationsExcluded
      ? registrationScopeCount
      : (audience.excludedRegistrationIds || []).length
    : 0;

  const isFilteredRegMode = [
    "date_time",
    "checked_in",
    "custom_form",
  ].includes(audience.registrationMode);

  const registrationCount =
    audience.registrationMode === "none" || audience.registrationMode === "disabled"
      ? 0
      : audience.allRegistrationsExcluded
      ? 0
      : audience.registrationMode === "custom_form"
      ? audience.customFormId
        ? Math.max(registrationScopeCount - excludedRegistrationCount, 0)
        : 0
      : audience.registrationMode === "registration_status"
      ? Math.max(registrationScopeCount - excludedRegistrationCount, 0)
      : isFilteredRegMode
      ? getRegistrationDateError(audience)
        ? 0
        : Math.max(registrationScopeCount - excludedRegistrationCount, 0)
      : audience.registrationMode === "all"
      ? registrationTotalCount
      : audience.registrationMode === "ticket_class"
      ? Math.max(registrationScopeCount - excludedRegistrationCount, 0) +
        audience.selectedRegistrationIds.length
      : audience.selectedRegistrationIds.length;

  const handleSelectAllRegistrations = () => {
    if (isFilterRegMode) {
      if (audience.registrationSearch?.trim()) {
        const searchIds = new Set(allMatchingRegistrationIds);
        setAudience((prev) => ({
          ...prev,
          allRegistrationsExcluded: false,
          excludedRegistrationIds: (prev.excludedRegistrationIds || []).filter(
            (id) => !searchIds.has(String(id))
          ),
        }));
      } else {
        setAudience((prev) => ({
          ...prev,
          allRegistrationsExcluded: false,
          excludedRegistrationIds: [],
        }));
      }
    } else if (audience.registrationMode === "selected") {
      setAudience((prev) => ({
        ...prev,
        allRegistrationsExcluded: false,
        selectedRegistrationIds: Array.from(
          new Set([
            ...(prev.selectedRegistrationIds || []).map(String),
            ...allMatchingRegistrationIds,
          ])
        ),
      }));
    }
  };

  const handleUnselectAllRegistrations = () => {
    if (isFilterRegMode) {
      if (audience.registrationSearch?.trim()) {
        setAudience((prev) => ({
          ...prev,
          excludedRegistrationIds: Array.from(
            new Set([
              ...(prev.excludedRegistrationIds || []).map(String),
              ...allMatchingRegistrationIds,
            ])
          ),
        }));
      } else {
        setAudience((prev) => ({
          ...prev,
          allRegistrationsExcluded: true,
          excludedRegistrationIds: allMatchingRegistrationIds,
        }));
      }
    } else if (audience.registrationMode === "selected") {
      if (audience.registrationSearch?.trim()) {
        const searchIds = new Set(allMatchingRegistrationIds);
        setAudience((prev) => ({
          ...prev,
          selectedRegistrationIds: (prev.selectedRegistrationIds || []).filter(
            (id) => !searchIds.has(String(id))
          ),
        }));
      } else {
        setAudience((prev) => ({
          ...prev,
          selectedRegistrationIds: [],
        }));
      }
    }
  };

  const selectedSummaryParts = [];
  if (audience.registrationMode === "disabled" || audience.registrationMode === "none") {
    selectedSummaryParts.push("All access disabled (0 registrations)");
  } else if (audience.registrationMode === "all") {
    selectedSummaryParts.push(`All ${registrationTotalCount} registrations (Open to all)`);
  } else if (audience.registrationMode === "ticket_class") {
    selectedSummaryParts.push(
      `${audience.ticketClassIds.length} ticket class(es) (${Math.max(
        registrationScopeCount - excludedRegistrationCount,
        0
      )} registrations)`
    );
  } else if (audience.registrationMode === "checked_in") {
    selectedSummaryParts.push("Checked-in registrations");
  } else if (audience.registrationMode === "selected") {
    selectedSummaryParts.push(`${audience.selectedRegistrationIds.length} specific registrations`);
  } else if (isFilteredRegMode) {
    selectedSummaryParts.push(`${registrationCount} filtered registrations`);
  }
  if (isFilterRegMode && excludedRegistrationCount > 0) {
    selectedSummaryParts.push(`${excludedRegistrationCount} unselected`);
  }

  const customFormOptions = useMemo(() => {
    return eventForms.map((f) => ({
      value: String(f.id),
      label: f.title || f.name || `Form #${f.id}`,
    }));
  }, [eventForms]);

  const selectedCustomForm = useMemo(
    () => eventForms.find((f) => String(f.id) === String(audience.customFormId)),
    [eventForms, audience.customFormId]
  );

  const customFormTicketClassIds = useMemo(() => {
    if (!selectedCustomForm) return [];
    let raw = selectedCustomForm.ticket_class_ids;
    if (Array.isArray(raw)) return raw.map(Number).filter(Boolean);
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed.map(Number).filter(Boolean);
      } catch {
        return raw.split(",").map(Number).filter(Boolean);
      }
    }
    if (selectedCustomForm.ticket_class_id) return [Number(selectedCustomForm.ticket_class_id)].filter(Boolean);
    return [];
  }, [selectedCustomForm]);

  const customFormAvailableTicketClasses = useMemo(() => {
    if (!customFormTicketClassIds.length) return ticketClasses;
    return ticketClasses.filter((tc) => customFormTicketClassIds.includes(Number(tc.id)));
  }, [ticketClasses, customFormTicketClassIds]);

  const handleApply = async () => {
    if (importLoading || importValidating || legacyImportLoading || legacyImportError) return;
    const dateErr = getRegistrationDateError(audience);
    if (dateErr) {
      toast.error(dateErr);
      return;
    }
    setSaving(true);
    try {
      let nextAccess = [{ role: "none" }];
      const isNoAccess =
        audience.registrationMode === "disabled" ||
        audience.registrationMode === "none";

      if (isNoAccess) {
        nextAccess = [{ role: "none" }];
      } else if (audience.registrationMode === "all") {
        nextAccess = [{ role: "all" }];
      } else if (
        ["ticket_class", "checked_in", "custom_form", "registration_status"].includes(audience.registrationMode) &&
        audience.ticketClassIds.length > 0
      ) {
        nextAccess = [
          {
            role: "attendee",
            scope: "selected",
            ticket_class_ids: audience.ticketClassIds.map(Number).filter(Boolean),
          },
        ];
      } else if (audience.registrationMode === "selected") {
        nextAccess = [
          {
            role: "attendee",
            scope: "selected",
            ticket_class_ids: (audience.ticketClassIds || []).map(Number).filter(Boolean),
            registration_ids: audience.selectedRegistrationIds,
          },
        ];
      } else if (audience.ticketClassIds.length > 0) {
        nextAccess = [
          {
            role: "attendee",
            scope: "selected",
            ticket_class_ids: audience.ticketClassIds.map(Number).filter(Boolean),
          },
        ];
      } else {
        nextAccess = [{ role: "none" }];
      }

      const audienceConfigPayload = {
        ...audience,
        category: "registrations",
        openToAll: audience.registrationMode === "all",
        registrationMode: audience.registrationMode,
        importedSubmissionIds: [],
        importedRegistrations: [],
        importedFileName: "",
        submission_ids: [],
      };

      await onSave({
        audienceType: isNoAccess ? "disabled" : (audience.registrationMode === "all" ? "all" : "registrations"),
        audienceConfig: audienceConfigPayload,
        access: nextAccess,
      });
      toast.success("Assignee settings saved.");
      onClose();
    } catch (e) {
      toast.error("Failed to save assignee settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div inert={importPopupOpen ? true : undefined} aria-hidden={importPopupOpen ? true : undefined} className="min-h-screen bg-gray-50 flex flex-col -m-4">
      {/* Top Sticky Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200 px-4 sm:px-6 md:px-8 py-3.5 flex items-center justify-between shadow-xs">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={onClose}
            aria-label={`Back to ${targetPluralLabel}`}
            className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
          >
            <FaArrowLeft className="h-3.5 w-3.5" />
          </button>
          <div>
            <h2 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2">
              <span>Assignee</span>
              <span className="text-gray-400 font-normal">|</span>
              <span className="text-blue-700 font-semibold truncate max-w-xs sm:max-w-md">
                {currentTarget?.title || currentTarget?.name || targetTypeLabel}
              </span>
            </h2>
            {currentTarget?.subtitle && (
              <p className="text-xs text-gray-500 mt-0.5">{currentTarget.subtitle}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-200 px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saving || importLoading || importValidating || legacyImportLoading || legacyImportError}
            onClick={handleApply}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#123047] px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-[#0b2235] transition-colors disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5 stroke-[3]" />
            )}
            Save Assignee
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 md:px-8 py-6 space-y-6">
        <section className="rounded-2xl border border-gray-100 bg-white p-6 sm:p-8 shadow-xs space-y-6">
          <div className="space-y-1">
            <h3 className="text-xl font-bold text-gray-900 tracking-tight">Audience Selection</h3>
            <p className="text-sm text-gray-500">
              Target which registrations can attend or access this {targetTypeLabel.toLowerCase()}.
            </p>
          </div>

          <div className="space-y-6 pt-2">
            {audienceOptionsLoading ? (
              <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500 flex flex-col items-center justify-center gap-3">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                <p>Loading audience options...</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Selected Summary Bar */}
                <div className="rounded-xl border border-gray-200 bg-slate-50/80 px-4 py-3 text-sm font-semibold text-slate-700 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-900 font-bold">Selected Audience:</span>
                    <span className="text-blue-700 font-bold">
                      {registrationSearchLoading
                        ? "Loading registrations..."
                        : `${registrationCount} registrations`}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 font-normal">
                    Total Available: <strong className="text-slate-800">{registrationTotalCount} registrations</strong>
                  </div>
                </div>

                {/* Registrations Panel */}
                <section className="min-w-0 rounded-xl border border-gray-200 bg-white p-5 sm:p-6 space-y-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-gray-100 pb-5">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="text-lg font-bold text-gray-900">Registrations Audience</h4>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        {registrationSearchLoading
                          ? "Searching registrations..."
                          : audience.registrationMode === "disabled" || audience.registrationMode === "none"
                          ? `All access is disabled by default. No registrations currently have access to this ${targetTypeLabel.toLowerCase()}.`
                          : audience.registrationSearch?.trim()
                          ? `${audienceOptions.registrations.length} of ${registrationFilteredCount} matching registrations`
                          : audience.registrationMode === "ticket_class"
                          ? `${registrationScopeCount} related registrations`
                          : audience.registrationMode === "registration_status"
                          ? `Filter registrations by status (${registrationFilteredCount} matched)`
                          : audience.registrationMode === "checked_in"
                          ? "Checked-in attendees in the selected ticket classes"
                          : audience.registrationMode === "date_time"
                          ? "Filter registrations by date & time"
                          : audience.registrationMode === "custom_form"
                          ? `Filter registrations by custom form submission (${registrationFilteredCount} matched)`
                          : `${registrationTotalCount} event registrations available`}
                      </p>
                    </div>

                    <div className="w-full sm:w-72">
                      <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">
                        Registration Mode
                      </label>
                      <CustomSelect
                        disabled={saving || importLoading || importValidating || legacyImportLoading}
                        options={REGISTRATION_MODE_OPTIONS}
                        value={audience.registrationMode}
                        onChange={(nextMode) => {
                          updateAudience("registrationMode", nextMode);
                          if (nextMode !== "date_time") {
                            updateAudience("registrationDateFrom", "");
                            updateAudience("registrationDateTo", "");
                          }
                          if (nextMode === "registration_status" && !audience.registrationStatusFilter) {
                            updateAudience("registrationStatusFilter", "shortlisted");
                          }
                          if (nextMode !== "custom_form") {
                            updateAudience("customFormId", "");
                            updateAudience("customFormStatus", "not_filled");
                          } else if (!audience.customFormId && eventForms.length > 0) {
                            updateAudience("customFormId", String(eventForms[0].id));
                          }
                        }}
                        size="sm"
                      />
                    </div>
                  </div>

                  {(audience.registrationMode === "disabled" || audience.registrationMode === "none") && (
                    <div className="rounded-xl border border-rose-200 bg-rose-50/70 p-5 text-sm font-semibold text-rose-900 flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center flex-shrink-0">
                        <X className="h-5 w-5 stroke-[2.5]" />
                      </div>
                      <div>
                        This {targetTypeLabel.toLowerCase()} has{" "}
                        <span className="font-extrabold text-rose-950">all access disabled</span>. No attendees can access this {targetTypeLabel.toLowerCase()} by default. Select a registration mode above to grant access.
                      </div>
                    </div>
                  )}

                  {audience.registrationMode === "all" && (
                    <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-5 text-sm font-semibold text-blue-900 flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0">
                        <Check className="h-5 w-5 stroke-[2.5]" />
                      </div>
                      <div>
                        This {targetTypeLabel.toLowerCase()} is{" "}
                        <span className="font-extrabold text-blue-950">Open to all</span>. Any attendee in this event{" "}
                        ({registrationTotalCount} registered attendees) will have access.
                      </div>
                    </div>
                  )}

                  {audience.registrationMode === "selected" && (
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <button type="button" disabled={importLoading || importValidating || legacyImportLoading || legacyImportError}
                          onClick={() => openImportPopup("file")}
                          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 disabled:opacity-50">
                          <Upload className="h-4 w-4" /> Import CSV
                        </button>
                        <button type="button" disabled={importLoading || importValidating || legacyImportLoading || legacyImportError}
                          onClick={() => openImportPopup("paste")}
                          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 disabled:opacity-50">
                          <FileText className="h-4 w-4" /> Paste IDs
                        </button>

                      </div>
                      <p className="text-xs text-gray-500">Import or paste Submission IDs to add attendees to your selection. You can also search and select below. Repeated imports keep existing selections.</p>
                      {legacyImportLoading && <p role="status" className="text-sm text-blue-700">Loading your previously imported selection…</p>}
                      {legacyImportError && <div role="alert" className="text-sm text-red-700">Could not load the previous imported selection. Retry before saving.
                        <button type="button" onClick={() => setLegacyRetry(value => value + 1)} className="ml-2 underline">Retry</button>
                      </div>}
                    </div>
                  )}



                  {audience.registrationMode === "date_time" && (
                    <div className="space-y-4 rounded-xl border border-gray-200 bg-slate-50/50 p-5">
                      <div>
                        <h5 className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-500" />
                          Registration Date &amp; Time Range
                        </h5>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Only attendees who registered between these dates will have access.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <label className="text-xs font-bold text-gray-700">From Date</label>
                          <input
                            type="datetime-local"
                            className={compactInputClass}
                            value={audience.registrationDateFrom || ""}
                            onChange={(e) =>
                              updateAudience("registrationDateFrom", e.target.value)
                            }
                          />
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-xs font-bold text-gray-700">To Date</label>
                          <input
                            type="datetime-local"
                            className={compactInputClass}
                            value={audience.registrationDateTo || ""}
                            onChange={(e) =>
                              updateAudience("registrationDateTo", e.target.value)
                            }
                          />
                        </div>
                      </div>
                      {getRegistrationDateError(audience) && (
                        <p className="text-xs text-red-600 font-semibold">
                          {getRegistrationDateError(audience)}
                        </p>
                      )}
                    </div>
                  )}

                  {audience.registrationMode === "custom_form" && (
                    <div className="space-y-4 rounded-xl border border-gray-200 bg-slate-50/50 p-5">
                      <div>
                        <h5 className="text-xs font-bold text-gray-800 uppercase tracking-wider">
                          Custom Form Filter
                        </h5>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Filter attendees based on custom form submission status.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <label className="text-xs font-bold text-gray-700">Select Custom Form</label>
                          <CustomSelect
                            options={customFormOptions}
                            value={audience.customFormId || ""}
                            onChange={(val) => {
                              updateAudience("customFormId", val);
                              updateAudience("ticketClassIds", []);
                            }}
                            placeholder="-- Select Form --"
                            searchable={true}
                            size="sm"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-xs font-bold text-gray-700">Submission Status</label>
                          <CustomSelect
                            options={CUSTOM_FORM_STATUS_OPTIONS}
                            value={audience.customFormStatus || "not_filled"}
                            onChange={(val) => updateAudience("customFormStatus", val)}
                            size="sm"
                          />
                        </div>
                      </div>
                      {selectedCustomForm && (
                        <div className="space-y-3 pt-2">
                          <CheckboxGrid
                            title={`Ticket Classes (Optional - ${selectedCustomForm.title || selectedCustomForm.name || "Form"})`}
                            items={customFormAvailableTicketClasses}
                            selectedIds={audience.ticketClassIds}
                            onChange={(ids) => updateAudience("ticketClassIds", ids)}
                            loading={false}
                            emptyText="No linked ticket classes found for this form."
                            getLabel={(ticket) => ticket.name || ticket.ticket_class_name || `Ticket #${ticket.id}`}
                          />
                          <p className="text-xs text-gray-500">
                            {audience.ticketClassIds && audience.ticketClassIds.length > 0
                              ? "Filtered to only the selected ticket class(es)."
                              : customFormTicketClassIds.length > 0
                              ? "No ticket class selected: applies to all ticket classes linked to this form."
                              : "No ticket class selected: applies to all ticket classes."}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {audience.registrationMode === "registration_status" && (
                    <div className="space-y-4 rounded-xl border border-gray-200 bg-slate-50/50 p-5">
                      <div>
                        <h5 className="text-xs font-bold text-gray-800 uppercase tracking-wider">
                          Registration Status Filter
                        </h5>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Filter attendees by ticket class and registration status (shortlisted, pending, un-shortlisted).
                        </p>
                      </div>
                      <div className="max-w-md space-y-1.5">
                        <label className="text-xs font-bold text-gray-700">Registration Status</label>
                        <CustomSelect
                          options={REGISTRATION_STATUS_FILTER_OPTIONS}
                          value={audience.registrationStatusFilter || "shortlisted"}
                          onChange={(val) => updateAudience("registrationStatusFilter", val)}
                          size="sm"
                        />
                      </div>
                      <div className="space-y-3 pt-2">
                        <CheckboxGrid
                          title="Ticket Classes (Optional - Select specific or leave unselected for All)"
                          items={ticketClasses}
                          selectedIds={audience.ticketClassIds}
                          onChange={(ids) => updateAudience("ticketClassIds", ids)}
                          loading={false}
                          emptyText="No ticket classes found."
                          getLabel={(ticket) => ticket.name || ticket.ticket_class_name || `Ticket #${ticket.id}`}
                        />
                        <p className="text-xs text-gray-500">
                          {audience.ticketClassIds && audience.ticketClassIds.length > 0
                            ? `Filtered to ${audience.ticketClassIds.length} selected ticket class(es).`
                            : "No ticket class checked: applies to all ticket classes."}
                        </p>
                      </div>
                    </div>
                  )}

                  {(audience.registrationMode === "ticket_class" ||
                    audience.registrationMode === "checked_in") && (
                    <div className="space-y-3">
                      <CheckboxGrid
                        title="Ticket Classes"
                        items={ticketClasses}
                        selectedIds={audience.ticketClassIds}
                        onChange={(ids) => updateAudience("ticketClassIds", ids)}
                        loading={false}
                        emptyText="No ticket classes found."
                        getLabel={(ticket) =>
                          ticket.name || ticket.ticket_class_name || `Ticket #${ticket.id}`
                        }
                      />
                      {audience.registrationMode === "checked_in" && (
                        <p className="rounded-lg bg-emerald-50 border border-emerald-100 p-3 text-xs font-semibold text-emerald-700">
                          Only registrations checked in at attendance in the selected ticket classes will have access.
                        </p>
                      )}
                    </div>
                  )}

                  {audience.registrationMode !== "all" &&
                    audience.registrationMode !== "none" &&
                    audience.registrationMode !== "disabled" &&
                    audience.registrationMode !== "checked_in" &&
                    (
                      <div className="space-y-4 pt-1">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="relative flex-1">
                            <Search className="pointer-events-none absolute left-3.5 top-3 h-4 w-4 text-gray-400" />
                            <input
                              className={`${inputClass} pl-10`}
                              placeholder={
                                audience.registrationMode === "ticket_class"
                                  ? "Search related registrations by name, email, phone, ticket..."
                                  : "Search registrations by name, email, phone, ticket..."
                              }
                              value={audience.registrationSearch}
                              onChange={(event) =>
                                updateAudience("registrationSearch", event.target.value)
                              }
                            />
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              type="button"
                              onClick={handleSelectAllRegistrations}
                              disabled={
                                registrationSearchLoading || legacyImportLoading || legacyImportError ||
                                displayedRegistrationRows.length === 0
                              }
                              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-2xs cursor-pointer"
                              title="Select all matching registrations"
                            >
                              <CheckSquare className="h-3.5 w-3.5 text-blue-600" />
                              <span>Select all</span>
                            </button>
                            <button
                              type="button"
                              onClick={handleUnselectAllRegistrations}
                              disabled={
                                registrationSearchLoading || legacyImportLoading || legacyImportError ||
                                displayedRegistrationRows.length === 0
                              }
                              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-2xs cursor-pointer"
                              title="Unselect all matching registrations"
                            >
                              <X className="h-3.5 w-3.5 text-gray-500" />
                              <span>Unselect all</span>
                            </button>
                          </div>
                        </div>

                        <AudiencePersonList
                          disabled={saving || legacyImportLoading || legacyImportError}
                          rows={displayedRegistrationRows}
                          cols={2}
                          isRegistration={true}
                          selectedIds={
                            isFilterRegMode
                              ? audience.excludedRegistrationIds
                              : audience.selectedRegistrationIds
                          }
                          allExcluded={isFilterRegMode && Boolean(audience.allRegistrationsExcluded)}
                          onChange={(ids, toggledId) => {
                            if (isFilterRegMode) {
                              if (audience.allRegistrationsExcluded) {
                                const otherExcluded = allMatchingRegistrationIds.filter(
                                  (x) => String(x) !== String(toggledId)
                                );
                                updateAudience("allRegistrationsExcluded", false);
                                updateAudience("excludedRegistrationIds", otherExcluded);
                              } else {
                                updateAudience("excludedRegistrationIds", ids);
                              }
                            } else {
                              updateAudience("selectedRegistrationIds", ids);
                            }
                          }}
                          invertSelection={isFilterRegMode}
                          emptyText={
                            registrationSearchLoading
                              ? "Searching registrations..."
                              : audience.registrationMode === "custom_form" && !audience.customFormId
                              ? "Select a custom form above to show matching registrations."
                              : audience.registrationMode === "ticket_class" &&
                                !audience.ticketClassIds.length
                              ? "Select ticket classes above to show related registrations."
                              : audience.registrationMode === "date_time" && !audience.registrationDateFrom && !audience.registrationDateTo
                              ? "Enter date & time range above to show registrations."
                              : audience.registrationSearch?.trim()
                              ? "No matching registrations found."
                              : audience.registrationMode === "registration_status"
                              ? "No registrations found matching the selected status and ticket class."
                              : audience.registrationMode === "custom_form"
                              ? "No registrations found matching the custom form criteria."
                              : audience.registrationMode === "ticket_class"
                              ? "No registrations found for the selected ticket classes."
                              : audience.registrationMode === "date_time"
                              ? "No registrations found for the selected date & time range."
                              : "No registrations found matching the criteria."
                          }
                        />

                        <div className="flex items-center justify-between gap-3 border-t border-gray-100 pt-3 text-xs text-gray-500">
                          <span>
                            {`Page ${registrationCurrentPage} ${registrationFilteredCount > 0
                              ? `• showing ${filteredRegistrations.length} of ${registrationFilteredCount}` : ""}`}
                          </span>
                          {(
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                className="rounded-md border border-gray-200 px-3 py-1.5 font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                                disabled={registrationSearchLoading || registrationPage <= 1}
                                onClick={() => setRegistrationPage((page) => Math.max(page - 1, 1))}
                              >
                                Previous
                              </button>
                              <button
                                type="button"
                                className="rounded-md border border-gray-200 px-3 py-1.5 font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                                disabled={
                                  registrationSearchLoading ||
                                  filteredRegistrations.length < 50
                                }
                                onClick={() => setRegistrationPage((page) => page + 1)}
                              >
                                Next
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                </section>
              </div>
            )}
          </div>
        </section>
      </div>
      {importPopupOpen && createPortal(
        <div className="fixed inset-0 z-[200] flex items-stretch justify-center bg-black/40 px-0 md:items-center md:px-3"
          onMouseDown={(event) => { if (event.target === event.currentTarget) closeImportPopup(); }}>
          <div ref={importDialogRef} role="dialog" aria-modal="true" aria-labelledby="assignee-import-title"
            tabIndex={-1}
            className="relative flex h-full w-full max-w-2xl flex-col border border-gray-100 bg-white shadow-2xl outline-none md:h-auto md:max-h-[90vh] md:rounded-md">
            <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-100 px-4 pb-3 pt-4 md:px-6 md:pt-6">
              <div className="space-y-1">
                <h2 id="assignee-import-title" className="text-lg font-semibold text-gray-900 md:text-xl">
                  {importInputMode === "file" ? "Import selected registrations" : "Paste Submission IDs"}
                </h2>
                <p className="text-xs text-gray-500">Add attendees to your current selection.</p>
              </div>
              <button type="button" onClick={closeImportPopup} aria-label="Close registration import"
                disabled={importLoading || importValidating}
                className="rounded-full p-2.5 text-gray-400 transition-all hover:bg-gray-100 hover:text-gray-900 disabled:opacity-30">
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4 md:px-6">
              {/* File Upload Mode (Matches Event Member Import) */}
              {importInputMode === "file" && (
                <div className="space-y-4">
                  {/* Template Download Banner */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100 group">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-white rounded-lg shadow-sm group-hover:scale-110 transition-transform">
                        <FileSpreadsheet className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-slate-900">Need a template?</h4>
                        <p className="text-xs text-slate-500">Download our pre-formatted CSV sheet.</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={downloadSampleCsv}
                      className="flex items-center gap-2 px-4 py-2 bg-white text-slate-900 text-sm font-bold rounded-lg border border-slate-200 hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-all active:scale-95 shadow-sm"
                    >
                      <Download className="w-4 h-4" />
                      Sample CSV
                    </button>
                  </div>

                  {/* Upload Zone */}
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIsDragging(true);
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setIsDragging(false);
                      const file = e.dataTransfer?.files?.[0];
                      if (file) {
                        handleFileUpload({ target: { files: [file], value: "" } });
                      }
                    }}
                    className={`relative min-h-[220px] rounded-xl border-2 border-dashed transition-all duration-300 flex flex-col items-center justify-center p-8 group cursor-pointer ${
                      isDragging
                        ? "border-indigo-500 bg-indigo-50"
                        : "border-gray-200 bg-gray-50/50 hover:bg-gray-50 hover:border-gray-300"
                    }`}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv, text/csv"
                      disabled={importLoading || importValidating || legacyImportLoading}
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <div className={`p-4 rounded-lg bg-white shadow-sm mb-4 transition-all duration-300 group-hover:-translate-y-1 ${
                      isDragging ? "scale-110 text-indigo-600" : "text-gray-400"
                    }`}>
                      {importLoading || importValidating ? (
                        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                      ) : (
                        <Upload className="w-8 h-8" />
                      )}
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-gray-900">
                        {importLoading || importValidating
                          ? "Validating Submission Ids..."
                          : "Drop your file here"}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        {importLoading || importValidating
                          ? "Checking IDs with this event..."
                          : "or click to browse from device"}
                      </p>
                      <p className="text-xs text-gray-400 mt-2">
                        Supports .csv with a <span className="font-semibold text-gray-600">Submission Id</span> column
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Paste IDs Mode (Card and inner title removed, single action button) */}
              {importInputMode === "paste" && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>
                      {pastedIdCount > 0
                        ? `${pastedIdCount} Submission Id(s) detected`
                        : "Comma, newline, or space separated"}
                    </span>
                    {pasteText ? (
                      <button
                        type="button"
                        onClick={() => setPasteText("")}
                        className="text-xs text-gray-500 hover:text-gray-700 transition-colors font-medium px-1"
                      >
                        Clear text
                      </button>
                    ) : null}
                  </div>
                  <textarea
                    rows={6}
                    value={pasteText}
                    onChange={(e) => setPasteText(e.target.value)}
                    placeholder="e.g. SUB1001, SUB1002, SUB1003 or paste a column of Submission Ids"
                    className="w-full rounded-xl border border-gray-300 p-3.5 text-xs font-mono text-gray-800 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 bg-slate-50/40 resize-y transition-all"
                  />
                </div>
              )}

              {/* Error Alert with Invalid IDs */}
              {importError && (
                <div className="rounded-xl border border-rose-200 bg-rose-50/80 p-4 space-y-2.5">
                  <div className="flex items-start gap-2.5">
                    <AlertCircle className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <h6 className="text-xs font-bold text-rose-900">
                        Validation Failed
                      </h6>
                      <p className="text-xs text-rose-700 mt-0.5">{importError}</p>
                    </div>
                  </div>

                  {importInvalidIds.length > 0 && (
                    <div className="pt-1">
                      <div className="text-[11px] font-bold text-rose-900 uppercase tracking-wider mb-1.5">
                        Invalid Submission Ids ({importInvalidIds.length}):
                      </div>
                      <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto pr-1">
                        {importInvalidIds.map((id) => (
                          <span
                            key={id}
                            className="inline-flex items-center gap-1 rounded-md border border-rose-300 bg-white px-2 py-0.5 text-xs font-mono font-medium text-rose-800 shadow-2xs"
                          >
                            <X className="h-3 w-3 text-rose-500" />
                            {id}
                          </span>
                        ))}
                      </div>
                      <p className="text-[11px] text-rose-600 mt-2 italic">
                        Please remove these invalid Submission Ids from your sheet or text and upload again.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {lastImport && (
                <div role="status" className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-xs text-emerald-800">
                  <CheckCircle2 className="mr-2 inline h-4 w-4" />
                  {lastImport.count} validated registrations added from {lastImport.fileName}. Existing selections are kept; duplicates are selected once.
                </div>
              )}
            </div>
            <div className="flex shrink-0 justify-end gap-3 border-t border-gray-100 px-4 py-3 md:px-6">
              {importInputMode === "paste" ? (
                <>
                  <button
                    type="button"
                    onClick={closeImportPopup}
                    disabled={importLoading || importValidating}
                    className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={importLoading || importValidating || !pasteText.trim()}
                    onClick={handlePasteSubmit}
                    className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 transition-all disabled:opacity-50 shadow-xs cursor-pointer"
                  >
                    {importValidating ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Validating...</span>
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4 stroke-[2.5]" />
                        <span>Add to selection {pastedIdCount > 0 ? `(${pastedIdCount})` : ""}</span>
                      </>
                    )}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={closeImportPopup}
                  disabled={importLoading || importValidating}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Done
                </button>
              )}
            </div>
          </div>
        </div>, document.body
      )}
    </div>
  );
};

export const ZoneAssigneeModal = (props) => (
  <SessionAssigneeModal {...props} entityType="Zone" />
);

export default SessionAssigneeModal;
